// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin.js';
import ChatWidget from 'components/ChatWidget/ChatWidget';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import { NavbarActionProvider } from 'contexts/NavbarActionContext';
import { AnalysesProvider } from 'contexts/AnalysesContext';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from 'routes.js';
import ProtectedRoute from 'components/ProtectedRoute';

// Custom Chakra theme
export default function Dashboard(props) {
	const { ...rest } = props;
	// states and functions
	const [fixed] = useState(false);
	const [toggleSidebar, setToggleSidebar] = useState(false);
	const [currentBrandText, setCurrentBrandText] = useState('');
	const location = useLocation();

	// Helper function, can be moved to a utils file if preferred
	const isWindowAvailable = () => typeof window !== 'undefined';

	useEffect(() => {
		if (isWindowAvailable()) {
			window.document.documentElement.dir = 'ltr';
		}
	});

	// This function was already present and is used as a fallback in getBrandText
	const getActiveRoute = (currentRoutes, pathname) => {
		let activeRoute = 'Default Brand Text'; // Default if no match
		for (let i = 0; i < currentRoutes.length; i++) {
			// Ignorer les routes cachées
			if (currentRoutes[i].hidden) {
				continue;
			}
			if (currentRoutes[i].collapse) {
				let collapseActiveRoute = getActiveRoute(currentRoutes[i].items, pathname);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (currentRoutes[i].category) {
				let categoryActiveRoute = getActiveRoute(currentRoutes[i].items, pathname);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				// More robust check for active route based on pathname
				if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
					return currentRoutes[i].name;
				}
			}
		}
		return activeRoute; // Return default if no specific route name is found
	};

	useEffect(() => {
		const getBrandText = (pathname) => {
			// Si on est sur une page de détails de mission, afficher "Missions"
			if (pathname.match(/^\/admin\/missions\/\d+$/)) {
				const missionsRoute = routes.find(r => r.path === '/missions' && !r.hidden);
				if (missionsRoute) {
					return missionsRoute.name;
				}
			}
			for (let i = 0; i < routes.length; i++) {
				// Ignorer les routes cachées
				if (routes[i].hidden) {
					continue;
				}
				// Exact match first
				if (routes[i].layout + routes[i].path === pathname) {
					return routes[i].name;
				}
			}
			// Fallback to the logic from existing getActiveRoute if no exact match
			// Pass location.pathname to it
			const activeRouteName = getActiveRoute(routes, pathname);
			return activeRouteName; // This will return 'Default Brand Text' or a found name
		};
		setCurrentBrandText(getBrandText(location.pathname));
	}, [location.pathname]);

	// functions for changing the states from components
	const getRoute = () => {
		return window.location.pathname !== '/admin/full-screen-maps';
	};

	// This function was already present
	const getActiveNavbar = (currentRoutes, pathname) => {
		let activeNavbar = false;
		for (let i = 0; i < currentRoutes.length; i++) {
			// Ignorer les routes cachées
			if (currentRoutes[i].hidden) {
				continue;
			}
			if (currentRoutes[i].collapse) {
				let collapseActiveNavbar = getActiveNavbar(currentRoutes[i].items, pathname);
				if (collapseActiveNavbar !== activeNavbar) {
					return collapseActiveNavbar;
				}
			} else if (currentRoutes[i].category) {
				let categoryActiveNavbar = getActiveNavbar(currentRoutes[i].items, pathname);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				// More robust check for active navbar based on pathname
				if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
					return currentRoutes[i].secondary;
				}
			}
		}
		return activeNavbar;
	};

	const getActiveNavbarText = (currentRoutes, pathname) => {
		let activeNavbarText = false; // Default value
		for (let i = 0; i < currentRoutes.length; i++) {
			// Ignorer les routes cachées
			if (currentRoutes[i].hidden) {
				continue;
			}
			if (currentRoutes[i].collapse) {
				let collapseActiveNavbarText = getActiveNavbarText(currentRoutes[i].items, pathname);
				if (collapseActiveNavbarText !== activeNavbarText) {
					return collapseActiveNavbarText;
				}
			} else if (currentRoutes[i].category) {
				let categoryActiveNavbarText = getActiveNavbarText(currentRoutes[i].items, pathname);
				if (categoryActiveNavbarText !== activeNavbarText) {
					return categoryActiveNavbarText;
				}
			} else {
				if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
					return currentRoutes[i].messageNavbar;
				}
			}
		}
		return activeNavbarText;
	};

	const getRoutes = (routes) => {
		return routes.map((route, key) => {
			if (route.layout === '/admin') {
				// Utiliser la permission définie directement dans l'objet route
				const requiredPermission = route.requiredPermission;

				return (
					<Route
						path={`${route.path}`}
						element={
							<ProtectedRoute
								requiredPermission={requiredPermission}
								allRoutes={routes}
							>
								{route.component}
							</ProtectedRoute>
						}
						key={key}
					/>
				);
			}
			// Gérer les catégories (category) et les collapses
			if (route.category && route.items) {
				return getRoutes(route.items);
			}
			if (route.collapse && route.items) {
				return getRoutes(route.items);
			}
			return null;
		});
	};
	document.documentElement.dir = 'ltr';
	const { onOpen } = useDisclosure();
	document.documentElement.dir = 'ltr';
	const isMissionCreatePage = location.pathname === '/admin/missions/create';
	const isDossierRegulationCreatePage = location.pathname === '/admin/dossier-regulation/create';
	const isMainPanelGlobalScrollOnly = isMissionCreatePage || isDossierRegulationCreatePage;
	return (
		<NavbarActionProvider>
			<AnalysesProvider>
				<Box>
					<Box>
						<SidebarContext.Provider
							value={{
								toggleSidebar,
								setToggleSidebar,
							}}
						>
							<Sidebar routes={routes} display="none" {...rest} />
							<Box
								float="right"
								minHeight="100vh"
								height="100%"
								overflow={isMainPanelGlobalScrollOnly ? 'visible' : 'auto'}
								position="relative"
								maxHeight={isMainPanelGlobalScrollOnly ? 'none' : '100%'}
								w={{ base: '100%', xl: 'calc( 100% - 240px )' }}
								maxWidth={{ base: '100%', xl: 'calc( 100% - 240px )' }}
								transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
								transitionDuration=".2s, .2s, .35s"
								transitionProperty="top, bottom, width"
								transitionTimingFunction="linear, linear, ease"
							>
								<Portal>
									<Box>
										<Navbar
											onOpen={onOpen}
											logoText={'Horizon UI Dashboard PRO'}
											brandText={currentBrandText}
											secondary={getActiveNavbar(routes, location.pathname)}
											message={getActiveNavbarText(routes, location.pathname)}
											fixed={fixed}
											{...rest}
										/>
									</Box>
								</Portal>

								{getRoute() ? (
									<Box
										mx="auto"
										p={{ base: '20px', md: '30px' }}
										pe="20px"
										minH={isMainPanelGlobalScrollOnly ? 'auto' : '100vh'}
										pt="50px"
									>
										<Routes>
											{getRoutes(routes)}
											<Route
												path="/"
												element={<Navigate to="/admin/default" replace />}
											/>
										</Routes>
									</Box>
								) : null}
								<Box>
									<Footer />
								</Box>
							</Box>
						</SidebarContext.Provider>
					</Box>
				</Box>
			</AnalysesProvider>
			{/* <ChatWidget /> */}
		</NavbarActionProvider>
	);
}	